/**
 * 目录生成设置弹框
 * 编辑发送给AI的提示词模板（目录要求部分）
 */
import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface OutlineGenSettings {
  outlineRequirementsPrompt: string;
}

interface OutlineGenerateSettingsDialogProps {
  show: boolean;
  onClose: () => void;
  onSave: (settings: OutlineGenSettings) => void;
  outlineRequirements: string;
  initialSettings?: OutlineGenSettings | null;
}

const DEFAULT_TEMPLATE = `🚨🚨🚨 强制性要求（必须100%遵守）🚨🚨🚨

你必须按照以下步骤操作：

第一步：仔细阅读"招标文件的目录要求"，识别出所有要求的章节名称
第二步：将识别出的每个章节名称作为一级目录的title，不要修改、不要改写、原样使用
第三步：为每个一级目录生成合理的二级和三级子目录
第四步：确保生成的一级目录数量与招标文件要求的完全一致

禁止行为：
❌ 不要只生成部分章节
❌ 不要修改章节名称
❌ 不要改变章节顺序
❌ 不要自行决定哪些章节"不重要"而省略`;

const OutlineGenerateSettingsDialog: React.FC<OutlineGenerateSettingsDialogProps> = ({
  show,
  onClose,
  onSave,
  outlineRequirements,
  initialSettings,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [prompt, setPrompt] = useState(DEFAULT_TEMPLATE);

  useEffect(() => {
    if (show) {
      setIsEditing(false);
      if (initialSettings) {
        setPrompt(initialSettings.outlineRequirementsPrompt);
      } else {
        setPrompt(outlineRequirements ? DEFAULT_TEMPLATE : '');
      }
    }
  }, [show, outlineRequirements, initialSettings]);

  const handleReset = () => {
    setIsEditing(false);
    setPrompt(outlineRequirements ? DEFAULT_TEMPLATE : '');
  };

  const handleSave = () => {
    onSave({ outlineRequirementsPrompt: prompt });
    onClose();
  };

  return (
    <Transition appear show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl rounded-xl bg-white shadow-2xl flex flex-col" style={{ height: '85vh' }}>
                {/* 头部 */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <span>⚙️</span> 生成目录设置
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* 内容区 */}
                <div className="flex-1 flex flex-col min-h-0 px-6 py-4">
                  <div className="flex items-center justify-between mb-3 flex-shrink-0">
                    <span className="text-sm font-medium text-gray-700">
                      目录要求提示词模板
                    </span>
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                      >
                        <PencilIcon className="w-3.5 h-3.5 mr-1" />
                        编辑
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsEditing(false)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                      >
                        <CheckIcon className="w-4 h-4 mr-1" />
                        保存
                      </button>
                    )}
                  </div>

                  <div
                    className={`flex-1 w-full p-3 border border-gray-300 rounded-lg bg-white shadow-sm min-h-0 ${
                      isEditing ? 'overflow-hidden' : 'overflow-y-auto'
                    }`}
                  >
                    {isEditing ? (
                      <textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        className="w-full h-full p-0 border-0 focus:ring-0 resize-none font-mono text-sm text-gray-900 overflow-y-auto"
                        placeholder="请输入提示词模板..."
                      />
                    ) : (
                      <div className="prose prose-sm max-w-none text-gray-800">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {prompt || '*（暂无）*'}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>

                {/* 底部 */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    重置默认
                  </button>
                  <div className="flex gap-3">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      取消
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      保存设置
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default OutlineGenerateSettingsDialog;
